import { useState, useEffect } from 'react';
import { useAccount, useNetwork, usePublicClient, useWalletClient, useContractRead } from 'wagmi';
import { ADDRESSES, CONFIG, TOKEN_NAMES, TOKEN_SYMBOLS, TOKEN_ICON_SYMBOLS, CHAIN_ID, TokenAddress } from '../config';
import { Abi, formatUnits, parseUnits } from 'viem';
import { FloppyDisk, Trash, Plus, Gear, Bank, Coins, Receipt, ArrowDown, ArrowUp, Clock } from '@phosphor-icons/react';
import { useAdmin } from '../hooks/useAdmin';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTokenAmount } from '../utils/formatting';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEntityDetails } from '../hooks/useEntityDetails';
import { useSettingsContract } from '../hooks/useSettingsContract';
import { useProduct } from '../contexts/ProductContext';
import { useAssetData } from '../hooks/useAssetData';
import { useTermsManagement } from '../hooks/useTermsManagement';
import { useParams } from 'react-router-dom';
import { useMetadataCreation } from '../hooks/useMetadata';
import { processImageData } from '../utils/ipfsUtils';

// Types
interface SettingsProps {
  entityAddress?: `0x${string}`;
  productAddress?: `0x${string}`;
  type?: 'trinity' | 'entity' | 'product';
  onProductDeployed?: (productAddress: `0x${string}`) => void;
}

interface TransactionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  asset: string;
  type: 'deposit' | 'withdraw';
  onSubmit: (amount: string) => void;
  balance: bigint;
  balance_deposit: bigint;
}

interface FormState {
  name: string;
  description: string;
  reclaimFee: string;
  redemptionFee: string;
  latePenalty: string;
  trinityServiceFee: string;
  trinityFee: string;
  resellValue: string;
  selectedAsset: string;
  maxWalletAmounts: Record<string, string>;
  propertyKey: string;
  propertyValue: string;
  uiPageIndex: string;
  uiPageContent: string;
}

// Helper Components
function TransactionDialog({ isOpen, onClose, asset, type, onSubmit, balance, balance_deposit }: TransactionDialogProps) {
  const [amount, setAmount] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-6 w-96 space-y-4">
        <h3 className="text-lg font-medium">
          {type === 'deposit' ? 'Deposit' : 'Withdraw'} {TOKEN_NAMES[asset]}
        </h3>
        <div className="space-y-2">
          <div className="text-sm text-gray-400">
            Available Balance: {type === 'deposit' ? formatUnits(balance_deposit || 0n, 18) : formatUnits(balance, 18)} {TOKEN_SYMBOLS[asset]}
          </div>
          <input
            type="text"
            className="input-field w-full bg-black/40"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              onSubmit(amount);
              onClose();
            }}
          >
            {type === 'deposit' ? 'Deposit' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GetUIPageDialog({ isOpen, onClose, index, content, pageCount, isLoading }: {
  isOpen: boolean;
  onClose: () => void;
  index: string;
  content: string;
  pageCount: string;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-6 w-[800px] max-h-[600px] space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">UI Page {index}</h3>
          <button
            className="text-gray-400 hover:text-white"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3">
              <div className="text-sm text-blue-300">
                <strong>Total UI Pages:</strong> {pageCount}
              </div>
              <div className="text-sm text-blue-300">
                <strong>Current Index:</strong> {index}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content:</label>
              <div className="bg-black/40 rounded p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-300 whitespace-pre-wrap break-all">
                  {content || '(empty)'}
                </pre>
              </div>
            </div>
            
            {content && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Preview:</label>
                <div className="bg-black/40 rounded p-4 max-h-96 overflow-y-auto border border-gray-600">
                  <div 
                    className="text-sm text-gray-300"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function GetUIImageDialog({ isOpen, onClose, index, imageData, imageUrl, isLoading }: {
  isOpen: boolean;
  onClose: () => void;
  index: string;
  imageData: string;
  imageUrl: string;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-6 w-[600px] max-h-[500px] space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">UI Image {index}</h3>
          <button
            className="text-gray-400 hover:text-white"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3">
              <div className="text-sm text-blue-300">
                <strong>Image Index:</strong> {index}
              </div>
              {imageUrl && (
                <div className="text-sm text-blue-300">
                  <strong>Image URL:</strong> {imageUrl}
                </div>
              )}
            </div>
            
            {imageData && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image Preview:</label>
                <div className="bg-black/40 rounded p-4 flex items-center justify-center">
                  <img 
                    src={imageData} 
                    alt={`UI Image ${index}`}
                    className="max-w-full max-h-64 object-contain rounded"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden text-sm text-gray-400">
                    Failed to load image
                  </div>
                </div>
              </div>
            )}
            
            {imageData && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Raw Data:</label>
                <div className="bg-black/40 rounded p-4 max-h-32 overflow-y-auto">
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap break-all">
                    {imageData.length > 200 ? `${imageData.substring(0, 200)}...` : imageData}
                  </pre>
                </div>
              </div>
            )}
            
            {!imageData && (
              <div className="text-center py-8 text-gray-400">
                No image data found for index {index}
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function GetRatingsDialog({ isOpen, onClose, averageRating, ratingCount, isLoading }: {
  isOpen: boolean;
  onClose: () => void;
  averageRating: number;
  ratingCount: number;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-6 w-[600px] space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Rating Statistics</h3>
          <button
            className="text-gray-400 hover:text-white"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading ratings...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">
                  {averageRating.toFixed(1)} / 5.0
                </div>
                <div className="text-sm text-yellow-300">
                  Average Rating
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 rounded p-3 text-center">
                <div className="text-2xl font-bold text-white">
                  {ratingCount}
                </div>
                <div className="text-sm text-gray-400">
                  Total Ratings
                </div>
              </div>
              
              <div className="bg-gray-800 rounded p-3 text-center">
                <div className="text-2xl font-bold text-white">
                  {ratingCount > 0 ? (averageRating / ratingCount * 100).toFixed(1) : '0'}%
                </div>
                <div className="text-sm text-gray-400">
                  Rating Percentage
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded p-3">
              <div className="text-sm text-gray-300">
                <strong>Details:</strong>
                <ul className="mt-2 space-y-1">
                  <li>• Average Rating: {averageRating.toFixed(1)} stars</li>
                  <li>• Total Ratings: {ratingCount} users</li>
                  <li>• Rating System: 1-5 stars</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export function Settings({ entityAddress, productAddress, type = 'product', onProductDeployed }: SettingsProps) {
  const { chain } = useNetwork();
  const { selectedProduct } = useProduct();
  const { productAddress: urlProductAddress } = useParams<{ productAddress: string }>();
  const { address: userAddress } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { data: trinityFee = 0n } = useContractRead({
    address: productAddress as `0x${string}`,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'trinityFee',
    enabled: chain?.id === CHAIN_ID && !!productAddress,
  }) as { data: bigint };

  // Get contract data
  const {
    name,
    description,
    reclaimFee,
    redemptionFee,
    latePenalty,
    trinityServiceFee,
    isLoading
  } = useSettingsContract({
    type,
    address: type === 'trinity' ? ADDRESSES.TRINITY_MASTER : type === 'entity' ? entityAddress : productAddress,
    enabled: true
  });

  // Get supported assets
  const { supportedAssets: assetSupportedAssets, balances: assetBalances } = useAssetData(productAddress);

  // Get terms management
  const {
    terms,
    isEditingTerms,
    isLoadingTerms,
    isUpdatingTerms,
    updateError: termsError,
    hasTermsChanged,
    newTermReturn,
    newTermDuration,
    setNewTermReturn,
    setNewTermDuration,
    setIsEditingTerms,
    setTerms,
    setOriginalTerms,
    addTerm,
    removeTerm,
    updateTerm,
    saveTerms,
    refetchTerms,
    cancelEdit: cancelTermsEdit
  } = useTermsManagement({ 
    productAddress: productAddress || undefined, 
    productAbi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi 
  });

  // Local state
  const [formState, setFormState] = useState<FormState>({
    name: '',
    description: '',
    reclaimFee: '30',
    redemptionFee: '2.5',
    latePenalty: '100',
    trinityServiceFee: '0',
    trinityFee: '1',
    resellValue: '',
    selectedAsset: '',
    maxWalletAmounts: {},
    propertyKey: '',
    propertyValue: '',
    uiPageIndex: '',
    uiPageContent: ''
  });

  const [transactionDialog, setTransactionDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    asset: '',
    type: 'deposit' as 'deposit' | 'withdraw',
    balance: 0n,
    balance_deposit: 0n
  });

  const [getUIPageDialog, setGetUIPageDialog] = useState({
    isOpen: false,
    index: '',
    content: '',
    pageCount: '',
    isLoading: false
  });

  const [getUIImageDialog, setGetUIImageDialog] = useState({
    isOpen: false,
    index: '',
    imageData: '',
    imageUrl: '',
    isLoading: false
  });

  const [getRatingsDialog, setGetRatingsDialog] = useState({
    isOpen: false,
    averageRating: 0,
    ratingCount: 0,
    isLoading: false
  });

  const [propertiesAddress, setPropertiesAddress] = useState<`0x${string}` | null>(null);

  const [retrievedProperty, setRetrievedProperty] = useState<{
    key: string;
    valueType: number;
    stringValue: string;
    uintValue: bigint;
    boolValue: boolean;
    bytesValue: string;
  } | null>(null);

  const [isUpdating, setIsUpdating] = useState(false);

  // Update form state when contract data changes
  useEffect(() => {
    if (!isLoading) {
      setFormState(prev => ({
        ...prev,
        name: name || prev.name,
        description: description || prev.description,
        reclaimFee: reclaimFee !== undefined ? formatUnits(reclaimFee, 2) : prev.reclaimFee,
        redemptionFee: redemptionFee !== undefined ? formatUnits(redemptionFee, 2) : prev.redemptionFee,
        latePenalty: latePenalty !== undefined ? formatUnits(latePenalty, 2) : prev.latePenalty,
        trinityServiceFee: trinityServiceFee !== undefined ? formatUnits(trinityServiceFee, 2) : prev.trinityServiceFee
      }));
    }
  }, [isLoading, name, description, reclaimFee, redemptionFee, latePenalty, trinityServiceFee]);

  // Add effect to fetch max wallet amounts when product is selected
  useEffect(() => {
    const fetchMaxWalletAmounts = async () => {
      if (!productAddress || !publicClient) return;

      try {
        const assets = await publicClient.readContract({
          address: productAddress,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
          functionName: 'getSupportedAssets',
        }) as string[];

        // Fetch max wallet amount for each asset
        const maxWalletAmounts = await Promise.all(
          assets.map(async (asset) => {
            const maxAmount = await publicClient.readContract({
        address: productAddress,
              abi: CONFIG.TRINITY_BOND_ABI.abi as unknown as Abi,
              functionName: 'maxPerWallet',
              args: [asset]
            }) as bigint;
            return { asset, maxAmount };
          })
        );

        // Update form state with max wallet amounts
        const formattedAmounts = maxWalletAmounts.reduce((acc, { asset, maxAmount }) => {
          acc[asset] = formatUnits(maxAmount, 18);
          return acc;
        }, {} as Record<string, string>);

        setFormState(prev => ({
          ...prev,
          maxWalletAmounts: formattedAmounts
        }));
      } catch (error) {
        console.error('Error fetching max wallet amounts:', error);
      }
    };

    fetchMaxWalletAmounts();
  }, [productAddress, publicClient]);

  // Add effect to fetch properties address when entity changes
  useEffect(() => {
    const fetchPropertiesAddress = async () => {
      if (!entityAddress || !publicClient || type !== 'entity') {
        setPropertiesAddress(null);
        return;
      }

      try {
        console.log('Fetching properties address for entity:', entityAddress);
        const propsAddress = await publicClient.readContract({
          address: entityAddress,
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          functionName: 'properties',
        }) as `0x${string}`;

        console.log('Properties address:', propsAddress);
        setPropertiesAddress(propsAddress);
      } catch (error) {
        console.error('Error fetching properties address:', error);
        setPropertiesAddress(null);
      }
    };

    fetchPropertiesAddress();
  }, [entityAddress, publicClient, type]);

  // Form handlers
  const handleFormChange = (field: keyof typeof formState, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBasic = async () => {
    if (!walletClient) return;

    try {
      const targetAddress = type === 'entity' ? entityAddress : productAddress;
      if (!targetAddress) {
        toast.error('No address available for saving settings');
        return;
      }

      const { request } = await publicClient.simulateContract({
        address: targetAddress,
        abi: type === 'entity' ? CONFIG.TRINITY_ENTITY_ABI.abi as Abi : CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'updateBasicSettings',
        args: [formState.name, formState.description]
      });

      const hash = await walletClient.writeContract(request);
      toast.success('Basic settings updated successfully');
    } catch (error) {
      console.error('Error updating basic settings:', error);
      toast.error('Failed to update basic settings');
    }
  };

  const handleSaveFees = async () => {
    if (!walletClient || !productAddress) return;

    try {
      const { request } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'updateAllFees',
        args: [{
          reclaimFee: BigInt(Math.floor(parseFloat(formState.reclaimFee) * 100)),
          redemptionFee: BigInt(Math.floor(parseFloat(formState.redemptionFee) * 100)),
          latePenalty: BigInt(Math.floor(parseFloat(formState.latePenalty) * 100)),
          trinityFee: parseUnits(formState.trinityFee, 18) // Convert to wei
        }]
      });

      const hash = await walletClient.writeContract(request);
      toast.success('Fees updated successfully');
    } catch (error) {
      console.error('Error updating fees:', error);
      toast.error('Failed to update fees');
    }
  };

  const handleAddAsset = async () => {
    if (!walletClient || !productAddress || !formState.selectedAsset) return;

    try {
      const { request } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'addAsset',
        args: [formState.selectedAsset]
      });

      const hash = await walletClient.writeContract(request);
      toast.success('Asset added successfully');
      setFormState(prev => ({ ...prev, selectedAsset: '' }));
    } catch (error) {
      console.error('Error adding asset:', error);
      toast.error('Failed to add asset');
    }
  };

  const handleRemoveAsset = async (asset: string) => {
    if (!walletClient || !productAddress) return;

    try {
      const { request } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'removeAsset',
        args: [asset]
      });

      const hash = await walletClient.writeContract(request);
      toast.success('Asset removed successfully');
    } catch (error) {
      console.error('Error removing asset:', error);
        toast.error('Failed to remove asset');
      }
  };

  const handleUpdateMaxWallets = async () => {
    if (!walletClient || !productAddress || !publicClient) return;

    try {
      console.log('Updating max wallets...');
      
      // Get supported assets from the product
      const assets = await publicClient.readContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
        functionName: 'getSupportedAssets',
      }) as string[];

      // Convert form values to wei amounts
      const maxAmountsInWei = assets.map(asset => {
        const amount = formState.maxWalletAmounts[asset] || '0';
        return parseUnits(amount, 18);
      });

      const { request: updateRequest } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as unknown as Abi,
        functionName: 'setMaxPerWallet',
        args: [assets, maxAmountsInWei],
        account: userAddress
      });

      console.log('Update request:', updateRequest);
      let updateHash: `0x${string}`;
      try {
        updateHash = await walletClient.writeContract(updateRequest);
        console.log('Update transaction hash:', updateHash);
      } catch (writeError) {
        console.error('Error writing contract:', writeError);
        toast.error(`Failed to send transaction: ${writeError instanceof Error ? writeError.message : 'Unknown error'}`);
        return;
      }

      // Wait for transaction to be mined
      let receipt;
      try {
        receipt = await publicClient.waitForTransactionReceipt({ 
          hash: updateHash,
          timeout: 60000 // 60 second timeout
        });
        console.log('Update transaction mined:', receipt);
        toast.success('Max wallets updated successfully');
      } catch (receiptError) {
        console.error('Error waiting for transaction receipt:', receiptError);
        toast.error(`Transaction sent but failed to confirm: ${receiptError instanceof Error ? receiptError.message : 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating max wallets:', error);
      toast.error('Failed to update max wallets');
    }
  };

  const handleSetResell = async () => {
    if (!userAddress || !walletClient || !publicClient) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!formState.resellValue) {
      toast.error('Please enter a resell value');
      return;
    }

    try {
      setIsUpdating(true);
      const resellValue = parseFloat(formState.resellValue);
      
      if (type === 'entity') {
        if (!entityAddress) {
          toast.error('No entity selected');
      return;
    }

        const { request } = await publicClient.simulateContract({
          account: userAddress,
          address: entityAddress,
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          functionName: 'setResell',
          args: [parseUnits(resellValue.toString(), 18)],
        });

        const hash = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash });
        toast.success('Resell value updated successfully');
      } else {
        if (!productAddress) {
          toast.error('No product selected');
          return;
        }

        const { request } = await publicClient.simulateContract({
          account: userAddress,
          address: productAddress,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
          functionName: 'setResell',
          args: [parseUnits(resellValue.toString(), 18)],
        });

        const hash = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash });
        toast.success('Resell value updated successfully');
      }
    } catch (error) {
      console.error('Error setting resell value:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to set resell value');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeposit = async (asset: string, amount: string) => {
    if (!walletClient || !productAddress) return;

    try {
      const { request } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'deposit',
        args: [asset, parseUnits(amount, 18)]
      });

      const hash = await walletClient.writeContract(request);
      toast.success('Deposit successful');
    } catch (error) {
      console.error('Error depositing:', error);
      toast.error('Failed to deposit');
    }
  };

  const handleWithdraw = async (asset: string, amount: string) => {
    if (!walletClient || !productAddress) return;

    try {
      const { request } = await publicClient.simulateContract({
        address: productAddress,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
        functionName: 'withdraw',
        args: [asset, parseUnits(amount, 18)]
      });

      const hash = await walletClient.writeContract(request);
        toast.success('Withdrawal successful');
    } catch (error) {
      console.error('Error withdrawing:', error);
      toast.error('Failed to withdraw');
    }
  };

  const handleSetProperty = async (key: string, value: string) => {
    if (!walletClient || !publicClient) {
      toast.error('Wallet not connected');
      return;
    }

    try {
      const address = type === 'trinity' ? ADDRESSES.TRINITY_MASTER : type === 'entity' ? entityAddress : productAddress;
      if (!address) {
        toast.error('Invalid address');
        return;
      }

      // For entities, we need to call the entity contract's setProperty function
      if (type === 'entity' && entityAddress) {
        const { request } = await publicClient.simulateContract({
          address: entityAddress,
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          functionName: 'setProperty',
          args: [key, value],
          account: userAddress,
        });

        const hash = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash });
        toast.success('Property set successfully');
        
        // Clear the form fields after successful submission
        setFormState(prev => ({
          ...prev,
          propertyKey: '',
          propertyValue: ''
        }));
      } else {
        // For other types, use the original logic
        const { request } = await publicClient.simulateContract({
          address,
          abi: type === 'trinity' ? CONFIG.TRINITY_ABI.abi as Abi : CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
          functionName: 'setProperty',
          args: [address, key, value],
          account: userAddress,
        });

        const hash = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash });
        toast.success('Property set successfully');
        
        // Clear the form fields after successful submission
        setFormState(prev => ({
          ...prev,
          propertyKey: '',
          propertyValue: ''
        }));
      }
    } catch (error) {
      console.error('Error setting property:', error);
      toast.error('Failed to set property');
    }
  };

  const handleGetProperty = async (key: string) => {
    if (!publicClient) {
      toast.error('Wallet not connected');
      return;
    }

    if (!key.trim()) {
      toast.error('Please enter a property key');
      return;
    }

    try {
      let propertyData;

      // For entities, we need to call the properties contract's getProperty function
      if (type === 'entity' && entityAddress && propertiesAddress) {
        console.log('Getting property from properties contract:', propertiesAddress);
        console.log('Entity address:', entityAddress);
        console.log('Property key:', key);

        propertyData = await publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getProperty',
          args: [entityAddress, key],
        }) as {
          key: string;
          valueType: number;
          stringValue: string;
          uintValue: bigint;
          boolValue: boolean;
          bytesValue: string;
        };

        console.log('Retrieved property data:', propertyData);
      } else if (type === 'trinity') {
        // For trinity master contract
        console.log('Getting property from trinity master contract');
        console.log('Property key:', key);

        propertyData = await publicClient.readContract({
          address: ADDRESSES.TRINITY_MASTER,
          abi: CONFIG.TRINITY_ABI.abi as Abi,
          functionName: 'getProperty',
          args: [key],
        }) as {
          key: string;
          valueType: number;
          stringValue: string;
          uintValue: bigint;
          boolValue: boolean;
          bytesValue: string;
        };

        console.log('Retrieved property data:', propertyData);
      } else if (type === 'product' && productAddress) {
        // For products, we need to check if the product has a getProperty function
        // If not, we'll try to get the properties contract from the product
        console.log('Getting property from product:', productAddress);
        console.log('Property key:', key);

        try {
          // First, try to call getProperty directly on the product contract
          propertyData = await publicClient.readContract({
            address: productAddress,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
            functionName: 'getProperty',
            args: [key],
          }) as {
            key: string;
            valueType: number;
            stringValue: string;
            uintValue: bigint;
            boolValue: boolean;
            bytesValue: string;
          };
        } catch (productError) {
          console.log('Product contract does not have getProperty function, trying to get properties contract...');
          
          // Try to get the properties contract from the product
          try {
            const productPropertiesAddress = await publicClient.readContract({
              address: productAddress,
              abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
              functionName: 'properties',
            }) as `0x${string}`;

            if (productPropertiesAddress && productPropertiesAddress !== '0x0000000000000000000000000000000000000000') {
              console.log('Product properties address:', productPropertiesAddress);
              
              propertyData = await publicClient.readContract({
                address: productPropertiesAddress,
                abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
                functionName: 'getProperty',
                args: [productAddress, key],
              }) as {
                key: string;
                valueType: number;
                stringValue: string;
                uintValue: bigint;
                boolValue: boolean;
                bytesValue: string;
              };
            } else {
              throw new Error('Product does not have a properties contract');
            }
          } catch (propertiesError) {
            console.error('Error getting product properties:', propertiesError);
            toast.error('This product does not support properties');
            return;
          }
        }

        console.log('Retrieved property data:', propertyData);
      } else {
        toast.error('Invalid contract type or missing address');
        return;
      }

      setRetrievedProperty(propertyData);
      
      // Display the appropriate value based on the value type
      let displayValue = '';
      switch (propertyData.valueType) {
        case 0: // String
          displayValue = propertyData.stringValue;
          break;
        case 1: // Uint
          displayValue = propertyData.uintValue.toString();
          break;
        case 2: // Bool
          displayValue = propertyData.boolValue.toString();
          break;
        case 3: // Bytes
          displayValue = propertyData.bytesValue;
          break;
        default:
          displayValue = 'Unknown type';
      }

      toast.success(`Property retrieved successfully. Value: ${displayValue}`);
    } catch (error) {
      console.error('Error getting property:', error);
      toast.error('Failed to get property. Property may not exist.');
      setRetrievedProperty(null);
    }
  };

  const handleSetUIPage = async (index: string, content: string) => {
    console.log('=== handleSetUIPage called ===');
    console.log('index:', index);
    console.log('content:', content);
    console.log('type:', type);
    console.log('entityAddress:', entityAddress);
    console.log('propertiesAddress:', propertiesAddress);
    console.log('walletClient:', !!walletClient);
    console.log('publicClient:', !!publicClient);
    console.log('userAddress:', userAddress);
    
    if (!walletClient || !publicClient) {
      console.log('Wallet not connected');
      toast.error('Wallet not connected');
      return;
    }

    try {
      // For entities, we need to call the properties contract directly
      if (type === 'entity' && entityAddress && propertiesAddress) {
        console.log('Using cached properties address:', propertiesAddress);

        // Test if we can access the properties contract
        console.log('Testing properties contract access...');
        try {
          const pageCount = await publicClient.readContract({
            address: propertiesAddress,
            abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
            functionName: 'getUIPageCount',
          });
          console.log('Current UI page count:', pageCount);
        } catch (testError) {
          console.error('Error testing properties contract access:', testError);
          toast.error('Cannot access properties contract');
          return;
        }

        // Check ownership of the properties contract
        console.log('Checking properties contract ownership...');
        const propertiesOwner = await publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'owner',
        }) as `0x${string}`;

        console.log('Properties owner:', propertiesOwner);
        console.log('User address:', userAddress);
        console.log('Is owner:', propertiesOwner === userAddress);

        if (propertiesOwner !== userAddress) {
          console.log('User is not the owner of the properties contract');
          toast.error('You are not authorized to set UI pages. Only the entity owner can set UI pages.');
          return;
        }

        console.log('Simulating contract call...');
        const { request } = await publicClient.simulateContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'setUIpage',
          args: [parseInt(index), content],
          account: userAddress,
        });

        console.log('Request object:', request);

        console.log('Writing contract...');
        let hash: `0x${string}`;
        try {
          console.log('About to call writeContract...');
          hash = await walletClient.writeContract(request);
          console.log('Transaction hash:', hash);
        } catch (writeError) {
          console.error('Error writing contract:', writeError);
          toast.error(`Failed to send transaction: ${writeError instanceof Error ? writeError.message : 'Unknown error'}`);
          return;
        }
        
        console.log('Waiting for transaction receipt...');
        let receipt;
        try {
          receipt = await publicClient.waitForTransactionReceipt({ 
            hash,
            timeout: 60000 // 60 second timeout
          });
          console.log('Transaction confirmed:', receipt);
        } catch (receiptError) {
          console.error('Error waiting for transaction receipt:', receiptError);
          toast.error(`Transaction sent but failed to confirm: ${receiptError instanceof Error ? receiptError.message : 'Unknown error'}`);
          return;
        }
        
        toast.success('UI page set successfully');
        
        // Clear the form fields after successful submission
        setFormState(prev => ({
          ...prev,
          uiPageIndex: '',
          uiPageContent: ''
        }));
      } else {
        console.log('Not an entity or no entity/properties address');
        toast.error('UI pages can only be set for entities');
      }
    } catch (error) {
      console.error('Error setting UI page:', error);
      if (error instanceof Error) {
        toast.error(`Failed to set UI page: ${error.message}`);
      } else {
        toast.error('Failed to set UI page');
      }
    }
  };

  const handleGetUIPage = async (index: string) => {
    if (!publicClient) {
      toast.error('Wallet not connected');
      return;
    }

    try {
      setGetUIPageDialog(prev => ({ ...prev, isLoading: true }));

      let targetPropertiesAddress: `0x${string}`;

      // Get properties address based on type
      if (type === 'entity' && entityAddress && propertiesAddress) {
        targetPropertiesAddress = propertiesAddress;
      } else if (type === 'product' && productAddress) {
        // Get properties address from product contract
        targetPropertiesAddress = await publicClient.readContract({
          address: productAddress,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
          functionName: 'properties',
        }) as `0x${string}`;
      } else {
        toast.error('No valid address available for getting UI page');
        setGetUIPageDialog(prev => ({ ...prev, isLoading: false }));
        return;
      }

      console.log('Getting UI page from properties address:', targetPropertiesAddress);

      // Get the UI page count first
      const pageCount = await publicClient.readContract({
        address: targetPropertiesAddress,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'getUIPageCount',
      }) as bigint;

      console.log('Total UI page count:', pageCount);

      // Get the UI page content
      const pageContent = await publicClient.readContract({
        address: targetPropertiesAddress,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'getUIPage',
        args: [parseInt(index)],
      }) as string;

      console.log('UI page content retrieved:', pageContent);

      setGetUIPageDialog({
        isOpen: true,
        index,
        content: pageContent,
        pageCount: pageCount.toString(),
        isLoading: false
      });
    } catch (error) {
      console.error('Error getting UI page:', error);
      toast.error(`Failed to get UI page: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setGetUIPageDialog(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleGetUIPageCount = async () => {
    if (!publicClient) {
      toast.error('Wallet not connected');
      return;
    }

    try {
      let targetPropertiesAddress: `0x${string}`;

      // Get properties address based on type
      if (type === 'entity' && entityAddress && propertiesAddress) {
        targetPropertiesAddress = propertiesAddress;
      } else if (type === 'product' && productAddress) {
        // Get properties address from product contract
        targetPropertiesAddress = await publicClient.readContract({
          address: productAddress,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
          functionName: 'properties',
        }) as `0x${string}`;
      } else {
        toast.error('No valid address available for getting UI page count');
        return;
      }

      console.log('Getting UI page count from properties address:', targetPropertiesAddress);

      // Get the UI page count
      const pageCount = await publicClient.readContract({
        address: targetPropertiesAddress,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'getUIPageCount',
      }) as bigint;

      console.log('Total UI page count:', pageCount);

      // Show the count in a toast
      toast.success(`Total UI pages: ${pageCount.toString()}`);

      // Also update the dialog state to show the count
      setGetUIPageDialog(prev => ({
        ...prev,
        pageCount: pageCount.toString()
      }));
    } catch (error) {
      console.error('Error getting UI page count:', error);
      toast.error(`Failed to get UI page count: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleGetUIImage = async (index: string) => {
    if (!propertiesAddress || !publicClient) {
      toast.error('Properties address not found');
      return;
    }

    setGetUIImageDialog(prev => ({ ...prev, isLoading: true, isOpen: true, index }));
    
    try {
      const result = await publicClient.readContract({
        address: propertiesAddress,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'getUIImage',
        args: [parseInt(index)]
      });

      const imageData = result as string;
      const imageUrl = imageData.includes('|') 
        ? `https://ipfs.io/ipfs/${imageData.split('|')[0]}/${imageData.split('|')[1]}`
        : imageData;

      setGetUIImageDialog(prev => ({ 
        ...prev, 
        isLoading: false, 
        imageData, 
        imageUrl 
      }));
    } catch (error) {
      console.error('Error getting UI image:', error);
      toast.error(`Failed to get UI image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setGetUIImageDialog(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleGetRatings = async () => {
    if (!propertiesAddress || !publicClient) {
      toast.error('Properties address not found');
      return;
    }

    setGetRatingsDialog(prev => ({ ...prev, isLoading: true, isOpen: true }));
    
    try {
      const [averageRating, ratingCount] = await Promise.all([
        publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getAverageRating',
          args: []
        }),
        publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getRatingCount',
          args: []
        })
      ]);

      setGetRatingsDialog(prev => ({ 
        ...prev, 
        isLoading: false, 
        averageRating: Number(averageRating),
        ratingCount: Number(ratingCount)
      }));
    } catch (error) {
      console.error('Error getting ratings:', error);
      toast.error(`Failed to get ratings: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setGetRatingsDialog(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Render sections
  const renderBasicSettings = () => (
      <div className="space-y-4">
      <h3 className="text-lg font-medium">Basic Settings</h3>
      <div className="space-y-4">
        <div>
          <div className="text-sm text-gray-400 mb-1">{entityAddress || productAddress}</div>
          <label className="block text-sm font-medium mb-1">Name</label>
        <input
          type="text"
          className="input-field w-full"
          value={formState.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
        />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
            className="input-field w-full"
          value={formState.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
        />
        </div>
        
        {/* Properties Address Display - Only for entities */}
        {type === 'entity' && propertiesAddress && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3">
            <div className="text-sm text-blue-300 font-medium mb-1">Properties Contract:</div>
            <div className="text-xs text-blue-200 font-mono break-all">
              {propertiesAddress}
            </div>
          </div>
        )}
        
        <button
          className="btn btn-primary"
          onClick={handleSaveBasic}
          disabled={isLoading}
        >
          Save Basic Settings
        </button>
      </div>
      <h2 className="text-xl font-bold text-white mb-4">Resell Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Resell Value (TRINITY)
            </label>
        <input
              type="number"
              step="0.000000000000000001"
              className="input-field w-full"
              placeholder="0.00"
              value={formState.resellValue}
              onChange={(e) => setFormState(prev => ({ ...prev, resellValue: e.target.value }))}
        />
          </div>
          <div className="flex justify-end">
        <button
              className="btn btn-primary"
              onClick={handleSetResell}
              disabled={!formState.resellValue}
        >
              Set Resell Value
        </button>
          </div>
        </div>

    </div>
  );

  const renderFeeSettings = () => (
    <motion.div
      className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-cyan-500/20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <h2 className="text-2xl font-bold mb-8">Fee Settings</h2>
      <div className="space-y-4">
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Trinity Fee (TRINITY)
          </label>
            <input
              type="number"
            step="0.000000000000000001"
              className="input-field w-full"
            value={Number(formatUnits(trinityFee, 18))}
            onChange={(e) => handleFormChange('trinityFee', e.target.value)}
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Redemption Fee (%)
          </label>
            <input
              type="number"
            step="0.01"
            className="input-field w-full"
            value={formState.redemptionFee}
            onChange={(e) => handleFormChange('redemptionFee', e.target.value)}
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Late Penalty (%)
          </label>
            <input
              type="number"
            step="0.01"
            className="input-field w-full"
            value={formState.latePenalty}
            onChange={(e) => handleFormChange('latePenalty', e.target.value)}
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Trinity Service Fee (%)
          </label>
            <input
              type="number"
            step="0.01"
            className="input-field w-full"
            value={formState.trinityServiceFee}
            onChange={(e) => handleFormChange('trinityServiceFee', e.target.value)}
            />
          </div>
        <div className="mt-4 flex justify-end">
        <button
            className="btn btn-primary"
          onClick={handleSaveFees}
        >
            Save Fees
        </button>
      </div>
      </div>
    </motion.div>
    );

  const renderTermsSettings = () => {
      if (!productAddress) return null;

    return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
        <motion.div
          className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-red-500/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
              <div className="flex items-center gap-3 mb-6">
                <Bank className="w-6 h-6 text-amber-400" />
                <h2 className="text-2xl font-bold">Terms Management</h2>
              </div>

          <div className="space-y-4">
            {/* Existing Terms List */}
                <div className="space-y-0 max-h-[200px] overflow-y-auto">
              <AnimatePresence>
                {terms.map((term, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                        className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg text-white">
                              {term.returnPercentage}% return in {term.duration} days
                        </span>
                      </div>
                    </div>
                    <button
                          onClick={() => {
                            const newTerms = terms.filter((_, i) => i !== index);
                            setTerms(newTerms);
                          }}
                      className="p-2 text-red-500 hover:text-red-400 transition-colors"
                    >
                      <Trash size={20} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Add New Term */}
            <div className="bg-black/20 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Return %
                  </label>
                  <input
                    type="number"
                    value={newTermReturn}
                    onChange={(e) => setNewTermReturn(e.target.value)}
                    placeholder="%"
                    className="input-field w-full bg-black/40"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Duration (days)
                  </label>
                  <input
                    type="number"
                    value={newTermDuration}
                    onChange={(e) => setNewTermDuration(e.target.value)}
                        placeholder="days"
                    className="input-field w-full bg-black/40"
                  />
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (newTermReturn && newTermDuration) {
                        setTerms([...terms, {
                          returnPercentage: newTermReturn,
                          duration: newTermDuration,
                          active: true
                        }]);
                        setNewTermReturn('');
                        setNewTermDuration('');
                      }
                    }}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Term
              </motion.button>
            </div>

            {/* Save Terms Changes */}
            {hasTermsChanged && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="btn btn-primary w-full mt-4"
                    onClick={async () => {
                      try {
                        const processedTerms = terms.map(term => ({
                          returnPercentage: Math.round(Number(term.returnPercentage) * 100),
                          duration: Math.round(Number(term.duration) * 86400),
                          active: term.active
                        }));
                        await saveTerms?.();
                        await refetchTerms?.();
                        setOriginalTerms(terms);
                      } catch (error) {
                        console.error('Failed to update terms:', error);
                      }
                    }}
                  >
                    Save Terms Changes
              </motion.button>
            )}
          </div>
        </motion.div>
          </div>
      </div>
    );
  };

  const renderAssetManagement = () => {
    if (type !== 'product' || !productAddress) return null;

    return (
        <div className="space-y-4">
        <h3 className="text-lg font-medium">Asset Management</h3>
            <div className="space-y-4">
          {/* Asset List */}
              <div className="space-y-2">
            {assetSupportedAssets.map((asset) => (
              <div
                    key={asset}
                    className="grid grid-cols-5 gap-4 p-4 bg-black/40 rounded-lg items-center"
                  >
                    {/* Asset Info */}
                    <div className="col-span-2 flex items-center gap-3">
                      <div className="text-2xl">
                    {TOKEN_ICON_SYMBOLS[asset] || '†'}
                      </div>
                      <div>
                        <div className="font-medium">
                      {TOKEN_NAMES[asset] || 'Unknown'}
                        </div>
                      </div>
                    </div>

                    {/* Balance */}
                    <div className="text-right font-medium">
                  {formatTokenAmount(assetBalances[asset] || 0n, asset)}
                    </div>

                    {/* Max Wallet Input */}
                <div>
                      <input
                        type="text"
                    className="input-field bg-black/40 text-sm w-full"
                        placeholder="Max amount"
                    value={formState.maxWalletAmounts[asset] || ''}
                        onChange={(e) => {
                      setFormState(prev => ({
                        ...prev,
                        maxWalletAmounts: {
                          ...prev.maxWalletAmounts,
                          [asset]: e.target.value
                        }
                      }));
                        }}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-center gap-2">
                  <button
                        className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                        onClick={() => setTransactionDialog({
                          isOpen: true,
                      asset,
                      type: 'deposit',
                      balance: assetBalances[asset] || 0n,
                      balance_deposit: assetBalances[asset] || 0n
                        })}
                      >
                    <span className="text-xs">Deposit</span>
                  </button>
                  <button
                        className="p-2 text-green-400 hover:text-green-300 transition-colors"
                        onClick={() => setTransactionDialog({
                          isOpen: true,
                      asset,
                      type: 'withdraw',
                      balance: assetBalances[asset] || 0n,
                      balance_deposit: assetBalances[asset] || 0n
                        })}
                      >
                    <span className="text-xs">Withdraw</span>
                  </button>
                  <button
                        className="p-2 text-red-500 hover:text-red-400 transition-colors"
                    onClick={() => handleRemoveAsset(asset)}
                      >
                        <Trash size={16} />
                  </button>
                    </div>
              </div>
                ))}
              </div>

              {/* Add New Asset */}
              <div className="mt-4 p-4 bg-black/20 rounded-lg">
                <div className="flex gap-2">
                  <input
                    type="text"
                value={formState.selectedAsset}
                onChange={(e) => setFormState(prev => ({ ...prev, selectedAsset: e.target.value }))}
                    placeholder="Asset Address (0x...)"
                    className="flex-1 input-field bg-black/40"
                  />
              <button
                    onClick={handleAddAsset}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Plus size={20} />
                    <span>Add Asset</span>
              </button>
                </div>
              </div>

              {/* Save Changes Button */}
              <div className="mt-6 flex justify-end">
            <button
                  onClick={handleUpdateMaxWallets}
                  className="btn btn-primary flex items-center gap-2"
              disabled={Object.keys(formState.maxWalletAmounts).length === 0}
                >
                  <FloppyDisk size={20} />
              <span>Update Max Wallets</span>
            </button>
              </div>
        </div>
      </div>
    );
  };

  const renderPropertySettings = () => (
    <div className="glass-panel p-6">
      <h2 className="text-xl font-bold text-white mb-4">Properties</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Property Key</label>
          <input
            type="text"
            className="input-field w-full"
            value={formState.propertyKey}
            onChange={(e) => handleFormChange('propertyKey', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Property Value</label>
          <textarea 
            className="input-field w-full bg-black/40"
            rows={4}
            value={formState.propertyValue}
            onChange={(e) => handleFormChange('propertyValue', e.target.value)}
          />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="btn btn-secondary"
          onClick={() => handleGetProperty(formState.propertyKey)}
        >
          Get Property
        </button>
        <button
          className="btn btn-primary"
          onClick={() => handleSetProperty(formState.propertyKey, formState.propertyValue)}
        >
          Set Property
        </button>
        {propertiesAddress && (
          <button
            type="button"
            className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
            onClick={async () => {
              try {
                console.log('Fetching comments for entity properties contract:', propertiesAddress);
                const comments = await publicClient.readContract({
                  address: propertiesAddress,
                  abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
                  functionName: 'getComments',
                  args: [entityAddress],
                });
                console.log('Fetched comments:', comments);
                toast.success('Fetched comments! Check console for details.');
              } catch (err) {
                console.error('Error fetching comments:', err);
                toast.error('Failed to fetch comments. See console for details.');
              }
            }}
          >
            GetComments (Debug)
          </button>
        )}
      </div>

      {/* Display retrieved property */}
      {retrievedProperty && (
        <div className="mt-4 p-4 bg-gray-700 rounded-lg max-h-96 overflow-y-auto">
          <h4 className="text-md font-semibold mb-2 text-white">Retrieved Property</h4>
          
          {/* Special handling for image property */}
          {retrievedProperty.key === 'image' && retrievedProperty.stringValue ? (
            <div className="space-y-2 text-sm text-gray-300">
              <div><strong>Key:</strong> {retrievedProperty.key}</div>
              <div><strong>Value Type:</strong> {retrievedProperty.valueType}</div>
              <div>
                <strong>Image:</strong>
                <div className="mt-2">
                  <img 
                    src={(() => {
                      const value = retrievedProperty.stringValue;
                      // If it's already a data URL, use it as is
                      if (value.startsWith('data:')) {
                        return value;
                      }
                      // If it's a URL, use it as is
                      if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('ipfs://')) {
                        return value;
                      }
                      // If it's base64 data without prefix, add the prefix
                      if (value.length > 100) { // Likely base64 data
                        return `data:image/png;base64,${value}`;
                      }
                      // Fallback to treating as URL
                      return value;
                    })()}
                    alt="Property Image"
                    className="max-w-full h-auto max-h-80 rounded border border-gray-600"
                    onError={(e) => {
                      console.error('Failed to load image:', e);
                      e.currentTarget.style.display = 'none';
                      // Show error message
                      const errorDiv = document.createElement('div');
                      errorDiv.className = 'text-red-400 text-xs mt-1';
                      errorDiv.textContent = 'Failed to load image';
                      e.currentTarget.parentNode?.appendChild(errorDiv);
                    }}
                    onLoad={(e) => {
                      console.log('Image loaded successfully');
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm text-gray-300">
              <div><strong>Key:</strong> {retrievedProperty.key}</div>
              <div><strong>Value Type:</strong> {retrievedProperty.valueType}</div>
              <div><strong>String Value:</strong> {retrievedProperty.stringValue || '(empty)'}</div>
              <div><strong>Uint Value:</strong> {retrievedProperty.uintValue?.toString() || '(empty)'}</div>
              <div><strong>Bool Value:</strong> {retrievedProperty.boolValue?.toString() || '(empty)'}</div>
              <div><strong>Bytes Value:</strong> {retrievedProperty.bytesValue || '(empty)'}</div>
            </div>
          )}
        </div>
      )}

      {/* UI Pages Section */}
      <div className="bg-gray-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-white">UI Pages</h3>
        <div className="text-sm text-blue-400 mb-4">
          ℹ️ Each entity has its own properties contract with separate UI pages.
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Page Index
            </label>
            <input
              type="number"
              min="0"
              value={formState.uiPageIndex}
              onChange={(e) => handleFormChange('uiPageIndex', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Page Data
            </label>
            <textarea
              value={formState.uiPageContent}
              onChange={(e) => handleFormChange('uiPageContent', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter page data..."
              rows={3}
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => {
              console.log('Set UI Page button clicked');
              console.log('uiPageIndex:', formState.uiPageIndex);
              console.log('uiPageContent:', formState.uiPageContent);
              handleSetUIPage(formState.uiPageIndex, formState.uiPageContent);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Set UI Page
          </button>
          <button
            type="button"
            onClick={() => {
              if (formState.uiPageIndex) {
                handleGetUIPage(formState.uiPageIndex);
              } else {
                toast.error('Please enter a page index first');
              }
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Get UI Page
          </button>
          <button
            type="button"
            onClick={() => {
              if (formState.uiPageIndex) {
                handleGetUIImage(formState.uiPageIndex);
              } else {
                toast.error('Please enter a page index first');
              }
            }}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            Get UIImage
          </button>
          <button
            type="button"
            onClick={() => {
              console.log('Get UI Page Count button clicked');
              handleGetUIPageCount();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            Get Page Count
          </button>
          <button
            type="button"
            onClick={() => {
              handleGetRatings();
            }}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            Get Ratings
          </button>
        </div>
        
        {getUIPageDialog.content && (
          <div className="mt-4 p-4 bg-gray-700 rounded-lg">
            <h4 className="text-md font-semibold mb-2 text-white">UI Page Data</h4>
            <div className="text-sm text-gray-300 mb-2">
              <strong>Total Pages:</strong> {getUIPageDialog.pageCount}
            </div>
            <div className="text-sm text-gray-300 mb-2">
              <strong>Page Index:</strong> {getUIPageDialog.index}
            </div>
            <div className="text-sm text-gray-300 mb-2">
              <strong>Raw Data:</strong>
            </div>
            <pre className="text-xs bg-gray-800 p-2 rounded overflow-auto max-h-32 text-gray-300">
              {getUIPageDialog.content}
            </pre>
            <div className="text-sm text-gray-300 mt-2">
              <strong>Preview:</strong>
            </div>
            <div className="text-xs bg-gray-800 p-2 rounded max-h-32 overflow-auto text-gray-300">
              {getUIPageDialog.content.length > 100 ? `${getUIPageDialog.content.substring(0, 100)}...` : getUIPageDialog.content}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Update TransactionDialog to handle deposits and withdrawals
  const handleTransactionSubmit = (amount: string) => {
    if (!transactionDialog.asset) return;

    if (transactionDialog.type === 'deposit') {
      handleDeposit(transactionDialog.asset, amount);
    } else {
      handleWithdraw(transactionDialog.asset, amount);
    }
  };

  // Main render
  return (
    <div className="space-y-8">
      <ToastContainer />
      <div className="grid grid-cols-2 gap-8">
        {renderBasicSettings()}
        <div className="grid grid-cols-1 gap-8">
        {renderPropertySettings()}
      </div>
      </div>
      {type === 'product' && (
        <>
          <div className="grid grid-cols-2 gap-8">
            {renderFeeSettings()}
            {renderTermsSettings()}
          </div>
          {renderAssetManagement()}
        </>
      )}
        <TransactionDialog
          isOpen={transactionDialog.isOpen}
        onClose={() => setTransactionDialog(prev => ({ ...prev, isOpen: false }))}
          asset={transactionDialog.asset}
          type={transactionDialog.type}
        onSubmit={handleTransactionSubmit}
        balance={transactionDialog.balance}
        balance_deposit={transactionDialog.balance_deposit}
        />
        <GetUIPageDialog
          isOpen={getUIPageDialog.isOpen}
          onClose={() => setGetUIPageDialog(prev => ({ ...prev, isOpen: false }))}
          index={getUIPageDialog.index}
          content={getUIPageDialog.content}
          pageCount={getUIPageDialog.pageCount}
          isLoading={getUIPageDialog.isLoading}
        />
        <GetUIImageDialog
          isOpen={getUIImageDialog.isOpen}
          onClose={() => setGetUIImageDialog(prev => ({ ...prev, isOpen: false }))}
          index={getUIImageDialog.index}
          imageData={getUIImageDialog.imageData}
          imageUrl={getUIImageDialog.imageUrl}
          isLoading={getUIImageDialog.isLoading}
        />
        <GetRatingsDialog
          isOpen={getRatingsDialog.isOpen}
          onClose={() => setGetRatingsDialog(prev => ({ ...prev, isOpen: false }))}
          averageRating={getRatingsDialog.averageRating}
          ratingCount={getRatingsDialog.ratingCount}
          isLoading={getRatingsDialog.isLoading}
        />
    </div>
  );
}